import React, { useEffect, useCallback } from "react";
import styles from "../../../../styles/AdminConsole.module.css";

/**
 * Modal
 *
 * A generic overlay + panel with a header and close button.
 * Clicking outside the panel or pressing Escape closes the modal.
 *
 * Props:
 *   title         string | node    — shown in the panel header
 *   onClose       () => void
 *   maxWidth      number | string  — CSS max-width of the panel (default 520)
 *   centerContent bool             — centres the panel vertically too (media preview)
 *   style         object           — extra inline styles on the panel
 *   children      node             — the modal body content
 *
 * Usage:
 *   <Modal title="Edit — Ravi Textiles" onClose={() => setEditingUser(null)}>
 *     <div className={styles.formInner}>…</div>
 *   </Modal>
 */
const Modal = React.memo(function Modal({
  title,
  onClose,
  maxWidth = 520,
  centerContent = false,
  style,
  children,
}) {
  // Close on Escape key
  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  return (
    <div
      className={`${styles.editOverlay} ${centerContent ? styles.editOverlayCenter : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.editPanel}
        style={{ maxWidth, ...style }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.editPanelHeader}>
          <span className={styles.statLabel}>{title}</span>
          <button className={styles.editPanelCloseBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        {children}
      </div>
    </div>
  );
});

export default Modal;
